import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Check, X, Trash2, Eye, Package, Printer } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatDate, outletNames } from "../lib/format";
import { useOutlets } from "../lib/useOutlets";
import { PageIntro, PanelHead, Modal, Field, SelectField, Badge, Pagination } from "../components/UI";
import { usePagination } from "../lib/usePagination";
import { ItemPicker } from "../components/ItemPicker";
import { useAuth } from "../context/AuthContext";
import { printStoreRequest } from "../lib/printDocs";

const PRIORITY_TONE = { low: "neutral", medium: "blue", high: "amber", urgent: "amber" };
const STATUS_TONE = { pending: "amber", fulfilled: "green", rejected: "neutral", cancelled: "neutral" };
const STATUS_LABEL = {
  pending: "Pending validation",
  fulfilled: "Fulfilled",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

const blankLine = () => ({ item_id: "", sku: "", name: "", unit: "pcs", qty: "1", notes: "", stock_at_pick: null });

const defaultForm = () => ({
  from_outlet: "main_wh",
  to_outlet: "kitchen",
  priority: "medium",
  requester_name: "",
  notes: "",
  items: [blankLine()],
});

export default function StoreRequestPage() {
  const { user } = useAuth();
  const isRequester = user?.role === "requester";
  const outletsList = useOutlets();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";
  const [srs, setSrs] = useState([]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null); // "form" | "detail" | "approve" | "reject"
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [approveLines, setApproveLines] = useState([]);
  const [approveComment, setApproveComment] = useState("");
  const [rejectComment, setRejectComment] = useState("");

  const isWarehouse = ["warehouse", "admin"].includes(user?.role);

  const loadAll = () => api.get("/store-requests").then((r) => setSrs(r.data));
  useEffect(() => {
    loadAll();
    api.get("/items").then((r) => setItems(r.data));
  }, []);

  const openNew = () => {
    setEditingId(null);
    setForm(defaultForm());
    setModal("form");
  };

  const openEdit = (sr) => {
    setEditingId(sr.id);
    setForm({
      from_outlet: sr.from_outlet || "main_wh",
      to_outlet: sr.to_outlet || "kitchen",
      priority: sr.priority || "medium",
      requester_name: sr.requester_name || "",
      notes: sr.notes || "",
      items: (sr.items || []).map((l) => ({
        item_id: l.item_id,
        sku: l.sku || "",
        name: l.name || "",
        unit: l.unit || "pcs",
        qty: String(l.qty),
        notes: l.notes || "",
        stock_at_pick: l.stock_at_request,
      })),
    });
    setModal("form");
  };

  const setLine = (idx, patch) => {
    const next = [...form.items];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, items: next });
  };
  const addLine = () => setForm({ ...form, items: [...form.items, blankLine()] });
  const removeLine = (idx) =>
    setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const onPickItem = (idx, id) => {
    const it = items.find((i) => i.id === id);
    if (!it) {
      setLine(idx, { item_id: "", sku: "", name: "", unit: "pcs", stock_at_pick: null });
      return;
    }
    setLine(idx, {
      item_id: it.id,
      sku: it.sku || "",
      name: it.name || "",
      unit: it.unit || "pcs",
      stock_at_pick: Number(it.stock || 0),
    });
  };

  const save = async (e) => {
    e.preventDefault();
    const clean = form.items.filter((l) => l.item_id && Number(l.qty) > 0);
    if (clean.length === 0) return toast.error("Add at least 1 item");
    if (isRequester && !(form.requester_name || "").trim()) return toast.error("Nama Pemesan wajib diisi");
    setSaving(true);
    try {
      const payload = {
        from_outlet: form.from_outlet,
        to_outlet: form.to_outlet,
        priority: form.priority,
        requester_name: form.requester_name || null,
        notes: form.notes,
        items: clean.map((l) => ({
          item_id: l.item_id,
          sku: l.sku,
          name: l.name,
          unit: l.unit,
          qty: Number(l.qty),
          notes: l.notes,
        })),
      };
      if (editingId) {
        await api.put(`/store-requests/${editingId}`, payload);
        toast.success("Store request updated");
      } else {
        await api.post("/store-requests", payload);
        toast.success("Store request created (pending warehouse validation)");
      }
      setModal(null);
      setEditingId(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (sr) => {
    if (!window.confirm(`Cancel store request ${sr.sr_number}?`)) return;
    try {
      await api.post(`/store-requests/${sr.id}/cancel`);
      toast.success(`${sr.sr_number} cancelled`);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openDetail = async (sr) => {
    try {
      const { data } = await api.get(`/store-requests/${sr.id}`);
      setDetail(data);
      setModal("detail");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openApprove = (sr) => {
    setDetail(sr);
    // Suggest qty_approved = min(requested, current stock)
    const currentStock = new Map(items.map((i) => [i.id, Number(i.stock || 0)]));
    setApproveLines(
      (sr.items || []).map((l) => {
        const stock = currentStock.get(l.item_id) ?? 0;
        return {
          qty_approved: String(Math.min(Number(l.qty), stock)),
          stock,
        };
      })
    );
    setApproveComment("");
    setModal("approve");
  };

  const submitApprove = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/store-requests/${detail.id}/approve`, {
        lines: approveLines.map((l, idx) => ({
          line_index: idx,
          qty_approved: Number(l.qty_approved || 0),
        })),
        comment: approveComment,
      });
      toast.success(`${detail.sr_number} fulfilled — stock deducted`);
      setModal(null);
      loadAll();
      api.get("/items").then((r) => setItems(r.data));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const openReject = (sr) => {
    setDetail(sr);
    setRejectComment("");
    setModal("reject");
  };

  const submitReject = async (e) => {
    e.preventDefault();
    if (!rejectComment.trim()) return toast.error("Reject reason is required");
    setSaving(true);
    try {
      await api.post(`/store-requests/${detail.id}/reject`, { comment: rejectComment });
      toast.success(`${detail.sr_number} rejected`);
      setModal(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const canEdit = (sr) =>
    sr.status === "pending" &&
    (sr.requester_username === user?.username || user?.role === "admin");
  const canCancel = canEdit;
  const canApprove = (sr) => sr.status === "pending" && isWarehouse;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = statusFilter ? srs.filter((s) => s.status === statusFilter) : srs;
    if (!q) return list;
    return list.filter(
      (s) =>
        s.sr_number.toLowerCase().includes(q) ||
        (s.requester_name || "").toLowerCase().includes(q) ||
        (s.to_outlet || "").toLowerCase().includes(q) ||
        (s.status || "").toLowerCase().includes(q)
    );
  }, [srs, search, statusFilter]);
  const pager = usePagination(filtered, 100, `${statusFilter}|${search}`);

  return (
    <>
      <PageIntro
        eyebrow="Operations · store request"
        title="Store requests"
        subtitle={
          isWarehouse
            ? "Validate outlet requests, approve with actual qty, or reject with a reason."
            : "Request stock from the warehouse — even items with 0 or insufficient stock."
        }
        testid="sr-title"
        action={
          <button data-testid="create-sr-button" className="primary-button" onClick={openNew}>
            <Plus size={17} /> New store request
          </button>
        }
      />
      <section className="panel">
        <div className="toolbar">
          <div className="search-box">
            <Search size={17} />
            <input
              data-testid="sr-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by SR#, requester, destination, status..."
            />
          </div>
          {statusFilter && (
            <button
              type="button"
              className="chip small"
              data-testid="sr-clear-status-filter"
              onClick={() => setSearchParams({})}
              title="Hapus filter status"
              style={{ marginLeft: 10 }}
            >
              Filter: {STATUS_LABEL[statusFilter] || statusFilter} <X size={12} />
            </button>
          )}
        </div>
        <PanelHead title="Store request list" detail={`${filtered.length} record(s)`} />
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>SR#</th>
                <th>Requester</th>
                <th>To outlet</th>
                <th>Items</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Created</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: 40 }}>
                    No store requests yet.
                  </td>
                </tr>
              )}
              {pager.pageItems.map((s) => (
                <tr key={s.id} data-testid={`sr-row-${s.id}`}>
                  <td>
                    <button className="text-button" onClick={() => openDetail(s)} data-testid={`sr-open-${s.id}`}>
                      <strong>{s.sr_number}</strong>
                    </button>
                  </td>
                  <td>{s.requester_name}<small>{s.requester_username}</small></td>
                  <td>{outletNames[s.to_outlet] || s.to_outlet}</td>
                  <td>{s.items?.length || 0}</td>
                  <td><Badge tone={PRIORITY_TONE[s.priority]}>{s.priority}</Badge></td>
                  <td>
                    <Badge tone={STATUS_TONE[s.status]}>{STATUS_LABEL[s.status] || s.status}</Badge>
                    {s.status === "fulfilled" && s.fulfilled_issue_number && (
                      <small>Issue: <b>{s.fulfilled_issue_number}</b></small>
                    )}
                  </td>
                  <td><small>{formatDate(s.created_at)}</small></td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button
                      data-testid={`sr-view-${s.id}`}
                      className="small-button"
                      onClick={() => openDetail(s)}
                    >
                      <Eye size={12} /> View
                    </button>
                    <button
                      data-testid={`sr-print-${s.id}`}
                      className="small-button"
                      style={{ marginLeft: 6 }}
                      onClick={() => printStoreRequest(s, items)}
                      title="Print store request"
                    >
                      <Printer size={12} /> Print
                    </button>
                    {canEdit(s) && (
                      <button
                        data-testid={`sr-edit-${s.id}`}
                        className="small-button"
                        style={{ marginLeft: 6 }}
                        onClick={() => openEdit(s)}
                      >
                        Edit
                      </button>
                    )}
                    {canApprove(s) && (
                      <>
                        <button
                          data-testid={`sr-approve-${s.id}`}
                          className="small-button success"
                          style={{ marginLeft: 6 }}
                          onClick={() => openApprove(s)}
                        >
                          <Check size={12} /> Validate
                        </button>
                        <button
                          data-testid={`sr-reject-${s.id}`}
                          className="small-button"
                          style={{ marginLeft: 6 }}
                          onClick={() => openReject(s)}
                        >
                          <X size={12} /> Reject
                        </button>
                      </>
                    )}
                    {canCancel(s) && (
                      <button
                        data-testid={`sr-cancel-${s.id}`}
                        className="small-button"
                        style={{ marginLeft: 6 }}
                        onClick={() => cancel(s)}
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onChange={pager.setPage} testid="sr-pagination" />
      </section>

      {modal === "form" && (
        <Modal
          title={editingId ? "Edit store request" : "New store request"}
          onClose={() => { setModal(null); setEditingId(null); }}
        >
          <form className="form-grid" onSubmit={save}>
            <SelectField
              label="From outlet"
              testid="sr-from-select"
              value={form.from_outlet}
              onChange={(v) => setForm({ ...form, from_outlet: v })}
              options={outletsList.map((o) => ({ value: o.code, label: o.name }))}
            />
            <SelectField
              label="To outlet"
              testid="sr-to-select"
              value={form.to_outlet}
              onChange={(v) => setForm({ ...form, to_outlet: v })}
              options={outletsList.filter((o) => o.type !== "warehouse").map((o) => ({ value: o.code, label: o.name }))}
            />
            <SelectField
              label="Priority"
              testid="sr-priority-select"
              value={form.priority}
              onChange={(v) => setForm({ ...form, priority: v })}
              options={["low", "medium", "high", "urgent"]}
            />
            {isRequester && (
              <label className="field">
                <span>Nama Pemesan *</span>
                <input
                  data-testid="sr-requester-name-input"
                  value={form.requester_name}
                  onChange={(e) => setForm({ ...form, requester_name: e.target.value })}
                  placeholder="Nama lengkap pemesan"
                />
              </label>
            )}
            <label className="field">
              <span>Notes</span>
              <input
                data-testid="sr-notes-input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Event, service period, etc."
              />
            </label>
            <div style={{ gridColumn: "1/-1" }}>
              <p className="eyebrow">Line items</p>
              <p className="form-hint" style={{ margin: "0 0 8px" }}>
                You can request any qty — even more than current stock. The warehouse team
                will validate and adjust to the qty they can fulfill.
              </p>
              <div className="line-editor">
                {form.items.map((l, idx) => (
                  <div className="line-row" key={idx} style={{ gridTemplateColumns: "2fr 90px 90px 1fr 36px" }}>
                    <ItemPicker
                      items={items}
                      value={l.item_id}
                      onChange={(id) => onPickItem(idx, id)}
                      testid={`sr-line-item-${idx}`}
                      placeholder="Search item..."
                    />
                    <input
                      data-testid={`sr-line-qty-${idx}`}
                      type="number"
                      min="0"
                      value={l.qty}
                      onChange={(e) => setLine(idx, { qty: e.target.value })}
                      placeholder="Qty"
                      required
                    />
                    <input
                      data-testid={`sr-line-unit-${idx}`}
                      value={l.unit}
                      readOnly
                      title="UOM inherited from item master"
                      style={{ background: "#f6f8f6", cursor: "not-allowed" }}
                    />
                    <input
                      data-testid={`sr-line-notes-${idx}`}
                      value={l.notes}
                      onChange={(e) => setLine(idx, { notes: e.target.value })}
                      placeholder={l.stock_at_pick != null ? `Stock: ${l.stock_at_pick} ${l.unit}` : "Notes"}
                    />
                    <button type="button" className="icon-button" onClick={() => removeLine(idx)} data-testid={`sr-line-remove-${idx}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <button type="button" data-testid="sr-add-line" className="chip small" onClick={addLine}>
                  <Plus size={12} /> Add item
                </button>
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => { setModal(null); setEditingId(null); }}>Cancel</button>
              <button data-testid="sr-save-button" className="primary-button" type="submit" disabled={saving || (isRequester && !(form.requester_name || "").trim())}>
                <Check size={16} /> {saving ? "Saving..." : editingId ? "Update request" : "Submit request"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "detail" && detail && (
        <Modal title={`Store request ${detail.sr_number}`} onClose={() => setModal(null)}>
          <SRDetailView sr={detail} items={items} />
          <div className="form-actions">
            <button
              data-testid="sr-detail-print"
              className="secondary-button"
              onClick={() => printStoreRequest(detail, items)}
            >
              <Printer size={16} /> Print store request
            </button>
            <button className="primary-button" onClick={() => setModal(null)}>Close</button>
          </div>
        </Modal>
      )}

      {modal === "approve" && detail && (
        <Modal title={`Validate ${detail.sr_number}`} onClose={() => setModal(null)}>
          <form className="form-grid" onSubmit={submitApprove}>
            <p className="form-hint" style={{ gridColumn: "1/-1" }}>
              Set the actual qty that can be issued. Suggested = min(requested, current stock).
              Approving deducts stock immediately and creates a linked stock-out (Issue).
            </p>
            <div className="table-wrap" style={{ gridColumn: "1/-1", maxHeight: 400, overflow: "auto" }}>
              <table>
                <thead>
                  <tr><th>#</th><th>Item</th><th>Requested</th><th>Stock now</th><th>Qty to issue</th></tr>
                </thead>
                <tbody>
                  {(detail.items || []).map((it, idx) => (
                    <tr key={idx} data-testid={`sr-approve-row-${idx}`}>
                      <td>{idx + 1}</td>
                      <td><strong>{it.name}</strong><small>{it.sku}</small></td>
                      <td>{it.qty} {it.unit}</td>
                      <td>
                        <Badge tone={approveLines[idx]?.stock >= it.qty ? "green" : "amber"}>
                          {approveLines[idx]?.stock ?? "-"} {it.unit}
                        </Badge>
                      </td>
                      <td>
                        <input
                          data-testid={`sr-approve-qty-${idx}`}
                          type="number"
                          min="0"
                          max={approveLines[idx]?.stock ?? undefined}
                          value={approveLines[idx]?.qty_approved ?? "0"}
                          onChange={(e) => {
                            const next = [...approveLines];
                            next[idx] = { ...next[idx], qty_approved: e.target.value };
                            setApproveLines(next);
                          }}
                          style={{ width: 100, padding: 8, border: "1px solid var(--line)", borderRadius: 5 }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <label className="field" style={{ gridColumn: "1/-1" }}>
              <span>Comment (optional)</span>
              <textarea
                data-testid="sr-approve-comment"
                value={approveComment}
                onChange={(e) => setApproveComment(e.target.value)}
                rows={2}
              />
            </label>
            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => setModal(null)}>Cancel</button>
              <button data-testid="sr-approve-confirm" className="primary-button" type="submit" disabled={saving}>
                <Package size={16} /> {saving ? "Issuing..." : "Validate & issue stock"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "reject" && detail && (
        <Modal title={`Reject ${detail.sr_number}`} onClose={() => setModal(null)}>
          <form className="form-grid" onSubmit={submitReject}>
            <p className="form-hint" style={{ gridColumn: "1/-1" }}>
              Rejecting is final. The requester will need to raise a new request if needed.
            </p>
            <label className="field" style={{ gridColumn: "1/-1" }}>
              <span>Reject reason (required)</span>
              <textarea
                data-testid="sr-reject-comment"
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                rows={3}
                autoFocus
                required
              />
            </label>
            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => setModal(null)}>Keep pending</button>
              <button data-testid="sr-reject-confirm" className="primary-button danger" type="submit" disabled={saving}>
                <X size={16} /> {saving ? "Rejecting..." : "Confirm reject"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}

function SRDetailView({ sr, items }) {
  return (
    <div className="pr-detail">
      <div className="pr-header-grid">
        <div><span className="lbl">Requester</span><strong>{sr.requester_name}</strong><small>@{sr.requester_username}</small></div>
        <div><span className="lbl">Created</span><strong>{formatDate(sr.created_at)}</strong></div>
        <div><span className="lbl">From</span><strong>{outletNames[sr.from_outlet] || sr.from_outlet}</strong></div>
        <div><span className="lbl">To</span><strong>{outletNames[sr.to_outlet] || sr.to_outlet}</strong></div>
        <div><span className="lbl">Priority</span><Badge tone={PRIORITY_TONE[sr.priority]}>{sr.priority}</Badge></div>
        <div><span className="lbl">Status</span><Badge tone={STATUS_TONE[sr.status]}>{STATUS_LABEL[sr.status]}</Badge></div>
      </div>
      {sr.notes && (
        <div className="pr-notes">
          <span className="lbl">Notes</span>
          <p>{sr.notes}</p>
        </div>
      )}
      <p className="eyebrow" style={{ marginTop: 16 }}>Line items</p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>#</th><th>Item</th><th>Qty requested</th><th>Stock at request</th><th>Stock now</th><th>Qty approved</th></tr>
          </thead>
          <tbody>
            {(sr.items || []).map((it, i) => {
              const master = items.find((m) => m.id === it.item_id);
              const currentStock = master ? Number(master.stock || 0) : null;
              return (
                <tr key={i} data-testid={`sr-detail-line-${i}`}>
                  <td>{i + 1}</td>
                  <td><strong>{it.name}</strong><small>{it.sku}</small></td>
                  <td>{it.qty} {it.unit}</td>
                  <td>{it.stock_at_request ?? "-"} {it.unit}</td>
                  <td>{currentStock ?? "-"} {it.unit}</td>
                  <td>
                    {it.qty_approved != null ? (
                      <Badge tone={it.qty_approved > 0 ? "green" : "neutral"}>{it.qty_approved} {it.unit}</Badge>
                    ) : (
                      <small>—</small>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {sr.review_comment && (
        <div className="pr-notes" style={{ marginTop: 12 }}>
          <span className="lbl">Warehouse comment</span>
          <p>{sr.review_comment}</p>
        </div>
      )}
      {sr.reject_reason && (
        <div className="pr-notes" style={{ marginTop: 12 }}>
          <span className="lbl">Reject reason</span>
          <p>{sr.reject_reason}</p>
        </div>
      )}
      {sr.status === "fulfilled" && sr.fulfilled_issue_number && (
        <p className="form-hint" style={{ marginTop: 12 }}>
          Fulfilled by stock-out <b>{sr.fulfilled_issue_number}</b> on {formatDate(sr.reviewed_at)}.
        </p>
      )}
    </div>
  );
}
